import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "productCheckbox",
    "contractCheckbox",
    "counterpartyCheckbox",
  ]
  static values = {
    productsToContracts: Object,
    counterpartiesToContracts: Object,
  }

  connect() {
    this.#buildContractToProductMap()
    this.#buildContractToCounterpartyMap()
  }

  toggleContracts(event) {
    const checkbox = event.target
    const productId = event.params.productId?.toString() || checkbox.value
    const contractIds = this.productsToContractsValue[productId] || []

    this.#checkRelatedContracts(contractIds, checkbox.checked)
  }

  toggleProduct(event) {
    const checkbox = event.target
    const contractId = checkbox.value

    const productId = this.#findProductIdForContract(contractId)
    if (productId) {
      this.#updateProductCheckbox(productId)
    }
  }

  toggleCounterparty(event) {
    const checkbox = event.target
    const contractId = checkbox.value

    const counterpartyId = this.#findCounterpartyIdForContract(contractId)
    if (counterpartyId) {
      this.#updateCounterpartyCheckbox(counterpartyId)
    }
  }

  toggleContractsFromCounterparty(event) {
    const checkbox = event.target
    const counterpartyId = checkbox.value
    const contractIds =
      this.counterpartiesToContractsValue[counterpartyId] || []

    this.#checkRelatedContracts(contractIds, checkbox.checked)

    contractIds.forEach((id) => {
      const productId = this.#findProductIdForContract(String(id))
      if (productId) {
        this.#updateProductCheckbox(productId)
      }
    })
  }

  #buildContractToProductMap() {
    this.contractToProduct = new Map()
    for (const [productId, contractIds] of Object.entries(
      this.productsToContractsValue,
    )) {
      for (const contractId of contractIds) {
        this.contractToProduct.set(String(contractId), productId)
      }
    }
  }

  #buildContractToCounterpartyMap() {
    this.contractToCounterparty = new Map()
    for (const [counterpartyId, contractIds] of Object.entries(
      this.counterpartiesToContractsValue,
    )) {
      for (const contractId of contractIds) {
        this.contractToCounterparty.set(String(contractId), counterpartyId)
      }
    }
  }

  #findContractCheckbox(id) {
    return this.contractCheckboxTargets.find(
      ({ value }) => value === String(id),
    )
  }

  #findProductCheckbox(productId) {
    return this.productCheckboxTargets.find(
      (checkbox) =>
        checkbox.value === productId ||
        checkbox.dataset.skzReportFilterProductIdParam === productId,
    )
  }

  #findCounterpartyCheckbox(counterpartyId) {
    return this.counterpartyCheckboxTargets.find(
      ({ value }) => value === String(counterpartyId),
    )
  }

  #findProductIdForContract(contractId) {
    return this.contractToProduct.get(contractId) || null
  }

  #findCounterpartyIdForContract(contractId) {
    return this.contractToCounterparty.get(contractId) || null
  }

  #checkRelatedContracts(contractIds, isChecked) {
    contractIds.forEach((id) => {
      const checkbox = this.#findContractCheckbox(id)
      if (checkbox) checkbox.checked = isChecked
    })
  }

  #hasCheckedContracts(contractIds) {
    return contractIds.some((id) => this.#findContractCheckbox(id)?.checked)
  }

  #updateProductCheckbox(productId) {
    const productCheckbox = this.#findProductCheckbox(productId)
    if (!productCheckbox) return

    const contractIds = this.productsToContractsValue[productId] || []
    productCheckbox.checked = this.#hasCheckedContracts(contractIds)
  }

  #updateCounterpartyCheckbox(counterpartyId) {
    const counterpartyCheckbox = this.#findCounterpartyCheckbox(counterpartyId)
    if (!counterpartyCheckbox) return

    const contractIds =
      this.counterpartiesToContractsValue[counterpartyId] || []
    counterpartyCheckbox.checked = this.#hasCheckedContracts(contractIds)
  }
}
